import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Baby, TrendingUp, TrendingDown, AlertTriangle, BookOpen, FileText, Sparkles, ArrowRight } from 'lucide-react';
import PagoCountdownCard from '../components/PagoCountdownCard';
import DashboardCommsCards from '../components/DashboardCommsCards';
import CreditosGratuitosCard from '../components/CreditosGratuitosCard';
import ContingencyBanner from '../components/ContingencyBanner';
import TesterModalitySwitcher from '../components/TesterModalitySwitcher';
import ParvulariaTour from '../components/ParvulariaTour';
import {
    MODULOS_PARVULARIA,
    INDICADORES_PARVULARIA,
    NIVEL_RIESGO_STYLE,
    HALLAZGOS_TRANSVERSALES_PV,
    computeStatsParvularia,
} from '../lib/parvularia-data';

const RIESGO_CLS = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
};

export default function DashboardParvularia() {
    const { user } = useAuth();
    const stats = useMemo(() => computeStatsParvularia(), []);

    // Ordenar por riesgo (mayor a menor)
    const priorityOrder = { 'Crítico': 5, 'Muy Alto': 4, 'Alto': 3, 'Medio-Alto': 3, 'Medio': 2, 'Bajo': 1 };
    const indicadoresPorRiesgo = useMemo(() => {
        return [...INDICADORES_PARVULARIA].sort((a, b) => (priorityOrder[b.riesgo] || 0) - (priorityOrder[a.riesgo] || 0));
    }, []);

    const criticos = indicadoresPorRiesgo.filter((i) => ['Crítico', 'Muy Alto'].includes(i.riesgo)).slice(0, 5);
    const favorables = [...INDICADORES_PARVULARIA]
        .filter((i) => i.riesgo === 'Bajo')
        .sort((a, b) => (b.logrado_2025 || 0) - (a.logrado_2025 || 0))
        .slice(0, 3);

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="dashboard-parvularia">
                {/* Countdown + banners comerciales */}
                <PagoCountdownCard />
                <TesterModalitySwitcher />
                <ContingencyBanner />
                <DashboardCommsCards user={user} />
                <CreditosGratuitosCard />

                {/* Hero Parvularia */}
                <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-pink-50 via-white to-sky-50 p-6 sm:p-8" data-testid="parvularia-hero">
                    <div className="flex items-start gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-pink-500 text-white shadow-md">
                            <Baby className="h-7 w-7" />
                        </div>
                        <div>
                            <div className="inline-flex items-center gap-1 rounded-full bg-pink-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-pink-800">
                                Portafolio 2026
                            </div>
                            <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                                Portafolio Educación Parvularia 2026
                            </h1>
                            <p className="mt-1 text-sm text-slate-600 leading-relaxed max-w-3xl">
                                Hola{user?.nombre ? `, ${user.nombre}` : ''}. Este espacio está dedicado exclusivamente a la evaluación
                                del <strong>Portafolio de Educación Parvularia</strong>, con rúbricas oficiales CPEIP 2026 y estadísticas
                                nacionales 2025 propias de este perfil docente.
                            </p>
                            <div className="mt-3">
                                <ParvulariaTour active={true} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* KPIs generales */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="parvularia-kpis">
                    <KpiCard title="Total rúbricas" value={stats.totalRubricas} desc="Módulos 1, 2 y 3" color="indigo" />
                    <KpiCard title="Módulo 1" value={stats.porModulo['pv-modulo-1']} desc="Planificación y evaluación" color="indigo" />
                    <KpiCard title="Módulo 2" value={stats.porModulo['pv-modulo-2']} desc="Experiencia grabada" color="emerald" />
                    <KpiCard title="Módulo 3" value={stats.porModulo['pv-modulo-3']} desc="Trabajo colaborativo" color="amber" />
                </div>

                {/* Módulos */}
                <div className="grid gap-4 md:grid-cols-3" data-testid="parvularia-modulos">
                    {MODULOS_PARVULARIA.map((m) => (
                        <Link key={m.id} to="/rubricas" className="block group">
                            <Card className="h-full border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all">
                                <CardContent className="p-5">
                                    <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${m.color === 'indigo' ? 'bg-indigo-100 text-indigo-700' : m.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {m.nombre}
                                    </div>
                                    <h3 className="mt-2 font-heading text-base font-bold text-slate-900 leading-snug">{m.titulo}</h3>
                                    <p className="mt-2 text-xs text-slate-500">
                                        {INDICADORES_PARVULARIA.filter((r) => r.modulo === m.id).length} rúbricas oficiales
                                    </p>
                                    <div className="mt-3 flex items-center text-xs font-medium text-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity">
                                        Ver rúbricas <ArrowRight className="ml-1 h-3 w-3" />
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>
                    ))}
                </div>

                {/* Mapa de Riesgo */}
                <Card>
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                            <div>
                                <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5 text-rose-600" />
                                    Mapa de riesgo — Educación Parvularia 2026
                                </h2>
                                <p className="text-xs text-slate-500">Basado en resultados nacionales 2025 y análisis oficial 2026.</p>
                            </div>
                            <Link to="/rubricas">
                                <Button variant="outline" size="sm" className="gap-1 text-xs">Ver todas <ArrowRight className="h-3 w-3" /></Button>
                            </Link>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                            {/* Indicadores críticos */}
                            <div>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-rose-700 mb-2 flex items-center gap-1">
                                    <TrendingDown className="h-3 w-3" /> Mayor prioridad de acompañamiento
                                </h3>
                                <div className="space-y-2" data-testid="criticos-list">
                                    {criticos.map((ind) => {
                                        const style = NIVEL_RIESGO_STYLE[ind.riesgo] || NIVEL_RIESGO_STYLE['Medio'];
                                        return (
                                            <div key={ind.id} className={`rounded-lg border ${RIESGO_CLS[style.color]} p-3`}>
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-[10px] font-mono font-bold opacity-70">{ind.codigo}</div>
                                                        <div className="text-xs font-bold leading-snug">{ind.nombre}</div>
                                                        <div className="mt-1 flex items-center gap-2 text-[11px]">
                                                            <span className="font-mono font-bold">{ind.logrado_2025}%</span>
                                                            <span className="opacity-70">Logrado 2025</span>
                                                        </div>
                                                    </div>
                                                    <Badge className={`shrink-0 ${style.color === 'rose' ? 'bg-rose-600 text-white' : 'bg-orange-600 text-white'}`}>
                                                        {style.emoji} {style.label}
                                                    </Badge>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* Indicadores favorables */}
                            <div>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2 flex items-center gap-1">
                                    <TrendingUp className="h-3 w-3" /> Fortalezas nacionales 2025
                                </h3>
                                <div className="space-y-2" data-testid="favorables-list">
                                    {favorables.map((ind) => (
                                        <div key={ind.id} className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] font-mono font-bold text-emerald-700 opacity-70">{ind.codigo}</div>
                                                    <div className="text-xs font-bold text-emerald-900 leading-snug">{ind.nombre}</div>
                                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-emerald-800">
                                                        <span className="font-mono font-bold">{ind.logrado_2025}%</span>
                                                        <span className="opacity-70">Logrado 2025</span>
                                                    </div>
                                                </div>
                                                <Badge className="shrink-0 bg-emerald-600 text-white">🟢 Bajo</Badge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Hallazgos transversales */}
                <Card>
                    <CardContent className="p-5">
                        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
                            <Sparkles className="h-5 w-5 text-indigo-600" />
                            Hallazgos transversales 2025 → 2026
                        </h2>
                        <ul className="space-y-2" data-testid="hallazgos-list">
                            {HALLAZGOS_TRANSVERSALES_PV.map((h, i) => (
                                <li key={i} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-indigo-500 shrink-0" />
                                    {h}
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>

                {/* CTAs */}
                <div className="grid gap-3 md:grid-cols-2">
                    <Link to="/rubricas" className="block">
                        <Card className="h-full border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white hover:shadow-md transition-all">
                            <CardContent className="p-5 flex items-center gap-4">
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-white shrink-0">
                                    <BookOpen className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="font-heading text-base font-bold text-slate-900">Rúbricas oficiales 2026</div>
                                    <div className="text-xs text-slate-600">Revisa las {stats.totalRubricas} rúbricas de Parvularia con claves para Básico, Competente y Destacado.</div>
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/borradores" className="block">
                        <Card className="h-full border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white hover:shadow-md transition-all">
                            <CardContent className="p-5 flex items-center gap-4">
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white shrink-0">
                                    <FileText className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="font-heading text-base font-bold text-slate-900">Mis borradores</div>
                                    <div className="text-xs text-slate-600">Crea, analiza con IA y perfecciona tus respuestas por rúbrica.</div>
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                </div>

                {/* Nota separación */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500 text-center" data-testid="separacion-nota">
                    Este dashboard muestra únicamente contenido de Educación Parvularia. Los indicadores, estadísticas y rúbricas
                    de Básica y Media se mantienen completamente separados.
                </div>
            </div>
        </Layout>
    );
}

function KpiCard({ title, value, desc, color }) {
    const clsMap = {
        indigo: 'text-indigo-700',
        emerald: 'text-emerald-700',
        amber: 'text-amber-700',
        pink: 'text-pink-700',
    };
    return (
        <Card>
            <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{title}</div>
                <div className={`mt-1 font-mono text-2xl font-extrabold ${clsMap[color] || 'text-slate-900'}`}>{value}</div>
                <div className="text-[11px] text-slate-500">{desc}</div>
            </CardContent>
        </Card>
    );
}
